import { computed, makeObservable } from "mobx";
import { BaseStore } from "stores/BaseStore";
import { normalizeGroupByConfiguration } from "components/Signals/Grouping/normalizeGroupByConfiguration";
import type {
    GroupByConfiguration,
    SignalsGroupByCatalogItem,
} from "components/Signals/SignalsModels";
import type ChartCardStore from "../ChartCardStore";

/**
 * Per-chart-card sub-store that owns what a chart is grouped by. Follows the OrderRulesStore
 * pattern: instantiated by ChartCardStore in its constructor, holds no state of its own, and
 * derives everything from the chart's saved vizOptions, its endpoint config, and the org's
 * group-by catalog.
 *
 * The division of labour is the same as the other feature stores. Deciding *whether and how* this
 * chart is grouped lives here. Acting on that answer -- pagination, stacking, which transforms run,
 * which axis a datum lands on -- stays in ChartCardStore, because those are chart concerns that
 * happen to read grouping.
 *
 * The grouping contract itself (the shapes, the readers, validation) lives one level further out in
 * `components/Signals/Grouping/` and knows nothing about charts. This store is the binding between
 * the two.
 */
class GroupingStore extends BaseStore {
    constructor(private chartStore: ChartCardStore) {
        super("Grouping Store");
        makeObservable(this);
    }

    /**
     * The grouping this chart is currently grouped by, or undefined when it is not grouped.
     *
     * This is the single request-time gate: the configuration must be complete, the endpoint must
     * not disable the option, and the option must be offered by the catalog. Grouping kinds are not
     * special-cased here -- the catalog decides what is available.
     *
     * An option the catalog does not list is unavailable, including while the catalog is empty
     * because it has not loaded. `ensureCatalogLoaded` is what keeps a refresh out of that window.
     *
     * Deliberately says nothing about compare-previous. The two are mutually exclusive, but that is
     * enforced where the user turns compare-previous on -- `ChartCardStore.toggleComparePrevious`
     * refuses while grouped, and the drawer disables grouping while comparing -- and applied where
     * the request is built, which drops grouping from the payload. Suppressing it here as well
     * would make a grouped chart also report itself unstacked and unpaginated mid-comparison.
     */
    @computed
    get activeGrouping(): GroupByConfiguration | undefined {
        const { config, parsedVizOptions } = this.chartStore;
        if (!config.grouping.enabled) return undefined;

        const grouping = normalizeGroupByConfiguration(
            parsedVizOptions,
            (option) => this.catalogEntryFor(option),
        );
        if (!grouping) return undefined;

        if (config.grouping.disabledGroupByNames.includes(grouping.option)) {
            return undefined;
        }

        return this.catalogEntryFor(grouping.option) ? grouping : undefined;
    }

    /**
     * The org's group-by catalog entry for `option`, or undefined when the catalog does not offer
     * it -- including while it has not loaded yet. Shared with OrderRulesStore so the two stores
     * agree on what the catalog says about an option instead of each re-scanning it.
     */
    public catalogEntryFor(
        option: string,
    ): SignalsGroupByCatalogItem | undefined {
        return this.chartStore.optionsStore.signalsGroupByCatalog.find(
            (item) => item.groupByOption === option,
        );
    }

    /** Whether this chart is grouped at all. The common question; `activeGrouping` is the detail. */
    @computed
    get isGrouped(): boolean {
        return !!this.activeGrouping;
    }

    /**
     * Which dimension the x-axis plots. False puts the chart's own dimension on x and the grouping
     * in the series; true swaps them.
     *
     * Temporal charts are always dated on x, so they never swap. Otherwise the user's saved choice
     * wins, defaulting to swapped -- which also catches older charts saved before the option
     * existed.
     */
    @computed
    get swapGroupingAndX(): boolean {
        const { config, parsedVizOptions } = this.chartStore;
        if (config.renderOptions.isTemporal || !this.isGrouped) {
            return false;
        }

        return parsedVizOptions?.swapGroupingAndX ?? true;
    }

    /**
     * The grouping half of the chart's cache key. Two charts differing only in grouping must not
     * share cached data, and a chart whose orientation changed re-partitions its percentages.
     */
    public getCacheSignature(): {
        groupBy: GroupByConfiguration | undefined;
        swapGroupingAndX: boolean;
    } {
        return {
            groupBy: this.activeGrouping,
            swapGroupingAndX: this.swapGroupingAndX,
        };
    }

    /**
     * Resolves once the group-by catalog is available. `activeGrouping` treats an option the
     * catalog does not list as unavailable, so a refresh must wait for it before building a request
     * or a cache key -- otherwise a grouped chart would briefly fetch itself ungrouped.
     *
     * Returns immediately for endpoints without grouping, and after the catalog's first load.
     */
    public ensureCatalogLoaded(): Promise<void> {
        return this.chartStore.config.grouping.enabled
            ? this.chartStore.optionsStore.ensureSignalsGroupByCatalogLoaded()
            : Promise.resolve();
    }
}

export default GroupingStore;
